"use client";

import { X, Copy, Check, ExternalLink, Heart, ThumbsUp, ThumbsDown, CheckCircle } from "lucide-react";
import { useState, useEffect, useRef, useId } from "react";
import { toggleFavorite, voteCoupon } from "@/app/actions";
import ModalNewsletter from "./ModalNewsletter";
import "./store/store-v5.css";

interface CouponModalProps {
    isOpen: boolean;
    onClose: () => void;
    coupon: {
        id: string;
        title: string;
        code?: string;
        description?: string;
        isFavorite?: boolean;
        type?: string;
        affiliateLink?: string;
    };
    store: {
        name: string;
        logo: string;
        affiliateLink: string;
    };
    session?: any;
    /** Store-bound signed token from the store page; sent as X-CS-Reveal. */
    revealToken?: string;
    /** REFERRAL pages only change the wording ("paste it in the signup form", "Did this code work?"); the flow is the same. */
    pageType?: "COUPON" | "REFERRAL";
}

export default function CouponModal({ isOpen, onClose, coupon, store, session, revealToken, pageType }: CouponModalProps) {
    const referral = pageType === "REFERRAL";
    const [copied, setCopied] = useState(false);
    const [isFav, setIsFav] = useState(coupon.isFavorite || false);
    const [showLoginAlert, setShowLoginAlert] = useState(false);
    const [voteStatus, setVoteStatus] = useState<"none" | "up" | "down">("none");

    // Secure Code Fetching
    const [fetchedCode, setFetchedCode] = useState<string | null>(null);
    const [loadingCode, setLoadingCode] = useState(false);

    const dialogRef = useRef<HTMLDialogElement>(null);
    const titleId = useId();
    const [nativeDialog, setNativeDialog] = useState(true);

    const isDeal = coupon.type === "DEAL";
    const displayCode = coupon.code || fetchedCode || "";

    useEffect(() => {
        setIsFav(coupon.isFavorite || false);
        setVoteStatus("none");
        setFetchedCode(null); // Reset when coupon changes
        setLoadingCode(false);
    }, [coupon.id, coupon.isFavorite]);

    // Fetch code on open if missing
    useEffect(() => {
        if (isOpen && !isDeal && !coupon.code && !fetchedCode && !loadingCode) {
            setLoadingCode(true);
            fetch(`/api/coupon/${coupon.id}/code`, revealToken ? { headers: { "X-CS-Reveal": revealToken } } : undefined)
                .then(res => res.json())
                .then(data => {
                    if (data.code) setFetchedCode(data.code);
                })
                .catch(err => console.error("Failed to fetch code:", err))
                .finally(() => setLoadingCode(false));
        }
    }, [isOpen, isDeal, coupon.code, fetchedCode, loadingCode, coupon.id, revealToken]);

    // Batch body style changes to avoid forced reflow
    useEffect(() => {
        requestAnimationFrame(() => {
            if (isOpen) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = 'unset';
            }
        });
        return () => {
            requestAnimationFrame(() => {
                document.body.style.overflow = 'unset';
            });
        };
    }, [isOpen]);

    // Native <dialog>: showModal() gives focus trapping, Escape, inert background and focus return to the opener.
    // React state and the DOM stay in step through the native "close" event (Escape, the X, a backdrop click).
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!isOpen || !dialog) return;
        if (typeof dialog.showModal !== "function") {
            // No <dialog> API: fall back to an always-open, labelled, modal-like region.
            setNativeDialog(false);
            return;
        }
        if (!dialog.open) dialog.showModal();
        // A stale "close" (queued by a cleanup close() just before a re-open, e.g. React Strict Mode) must not
        // close the freshly opened dialog: only react when it really is closed.
        const handleClose = () => { if (!dialog.open) onClose(); };
        dialog.addEventListener("close", handleClose);
        return () => {
            dialog.removeEventListener("close", handleClose);
            if (dialog.open) dialog.close();
        };
        // onClose is intentionally not a dependency: the parent re-creates it every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        if (nativeDialog || !isOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nativeDialog, isOpen]);

    if (!isOpen) return null;

    // Ask the dialog to close; its "close" event then calls onClose. Without the API, call onClose directly.
    const requestClose = () => {
        const dialog = dialogRef.current;
        if (nativeDialog && dialog?.open) dialog.close();
        else onClose();
    };

    const handleCopy = async () => {
        if (!displayCode) return;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(displayCode);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = displayCode;
                textArea.style.position = "fixed";
                textArea.style.left = "-9999px";
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                } catch (err) {
                    console.error('Fallback: Oops, unable to copy', err);
                }
                document.body.removeChild(textArea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleFavorite = async () => {
        setIsFav(!isFav);
        setShowLoginAlert(false);

        const res = await toggleFavorite(coupon.id);
        if (!res.success) {
            setIsFav(prev => !prev);
            if (res.message === "Must be logged in") {
                setShowLoginAlert(true);
            }
        }
    };

    const handleVote = async (type: "UP" | "DOWN") => {
        if (voteStatus !== "none") return;
        setVoteStatus(type === "UP" ? "up" : "down");
        await voteCoupon(coupon.id, type);
    };

    return (
        <dialog
            ref={dialogRef}
            className="sp-dialog"
            aria-labelledby={titleId}
            // Progressive enhancement: closes on a click outside where supported; the X, Escape and the
            // backdrop handler below do not depend on it.
            {...({ closedby: "any" } as Record<string, string>)}
            {...(!nativeDialog ? { open: true, role: "dialog", "aria-modal": true } : {})}
            onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}
        >
            <div className="sp-dialog__scroll">
                <button type="button" onClick={requestClose} className="sp-dialog__close" aria-label="Close">
                    <X className="w-6 h-6" aria-hidden="true" />
                </button>

                <div className="text-center">
                    <p className="sp-dialog__store">{store.name}</p>
                    <h2 id={titleId} className="sp-dialog__title">{coupon.title}</h2>

                    <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4">
                        {isDeal ? "Deal activated! No code required." : referral ? "Copy the code below and paste it in the signup form, before you create your account." : "Copy the code below and paste it at checkout."}
                    </p>

                    {/* Code Box */}
                    {!isDeal ? (
                        <button
                            type="button"
                            onClick={handleCopy}
                            className="sp-dialog__code"
                            aria-label={loadingCode ? "Revealing code" : `Copy code ${displayCode}`}
                        >
                            {loadingCode ? (
                                <span className="flex flex-col items-center gap-2">
                                    <span className="w-6 h-6 border-2 border-[#00ff9d]/30 border-t-[#00ff9d] rounded-full animate-spin"></span>
                                    <span className="text-xs text-[#00ff9d] animate-pulse">Revealing Code...</span>
                                </span>
                            ) : (
                                <>
                                    <code>{displayCode}</code>
                                    {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                                </>
                            )}
                        </button>
                    ) : (
                        <div className="sp-dialog__deal">
                            <p className="text-base sm:text-lg font-bold text-[#00ff9d] mb-1">Deal Activated!</p>
                            <p className="text-gray-400 text-xs sm:text-sm">No coupon code required.</p>
                        </div>
                    )}
                    <p className="sr-only" role="status">{copied ? "Code copied to clipboard" : ""}</p>

                    <div className="flex flex-col gap-2 sm:gap-3">
                        {/* 1. GO TO STORE - FIRST */}
                        {/*
                            Outbound traffic goes through /out/{couponId} like every
                            other card (CouponChecker T-108): that route resolves
                            coupon link -> store link -> store websiteUrl and skips a
                            link known to be dead, so this CTA can no longer send a
                            visitor into a shortener 404. `rel`/`target` unchanged.
                        */}
                        <a
                            href={`/out/${coupon.id}`}
                            target="_blank"
                            rel="nofollow noopener noreferrer sponsored"
                            onClick={handleCopy}
                            className="btn-primary w-full py-3 sm:py-4 text-base sm:text-lg flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all shadow-[0_0_30px_rgba(0,255,157,0.3)]"
                        >Go to {store.name} <ExternalLink className="w-4 h-4" aria-hidden="true" /></a>

                        {/* 2. VOTING SECTION - AFTER CTA */}
                        <div className="bg-gradient-to-br from-purple-500/10 to-[#00ff9d]/10 border border-white/10 rounded-xl p-3 sm:p-4">
                            <div className="flex items-center justify-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#00ff9d]" aria-hidden="true" />
                                <p className="text-sm sm:text-base font-bold text-white uppercase tracking-wide">
                                    {referral ? "Did this code work?" : "Did this coupon work?"}
                                </p>
                            </div>

                            {/* Motivational text */}
                            <p className="text-xs sm:text-sm text-gray-400 mb-3">
                                {session
                                    ? "Vote & earn +5 coins!"
                                    : "Help others find working codes!"}
                            </p>

                            {/* Voting Buttons - GREEN YES, RED NO - Responsive */}
                            <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3">
                                <button
                                    onClick={() => handleVote("UP")}
                                    disabled={voteStatus !== "none"}
                                    className={`flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all w-full sm:w-auto ${voteStatus === "up"
                                        ? "bg-[#00ff9d] text-black scale-105 shadow-lg shadow-[#00ff9d]/30"
                                        : voteStatus === "none"
                                            ? "bg-[#00ff9d]/20 hover:bg-[#00ff9d]/40 text-[#00ff9d] border border-[#00ff9d]/50 hover:scale-105"
                                            : "opacity-30 cursor-not-allowed text-gray-500 bg-white/5"
                                        }`}
                                >
                                    <ThumbsUp className="w-4 h-4" aria-hidden="true" />
                                    Yes, it worked!
                                </button>
                                <button
                                    onClick={() => handleVote("DOWN")}
                                    disabled={voteStatus !== "none"}
                                    className={`flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl font-bold text-xs sm:text-sm transition-all w-full sm:w-auto ${voteStatus === "down"
                                        ? "bg-red-500 text-white scale-105 shadow-lg shadow-red-500/30"
                                        : voteStatus === "none"
                                            ? "bg-red-500/20 hover:bg-red-500/40 text-red-400 border border-red-500/50 hover:scale-105"
                                            : "opacity-30 cursor-not-allowed text-gray-500 bg-white/5"
                                        }`}
                                >
                                    <ThumbsDown className="w-4 h-4" aria-hidden="true" />
                                    No
                                </button>
                            </div>

                            {/* Thank you message after voting */}
                            {voteStatus !== "none" && (
                                <div className="mt-3 p-2 sm:p-3 bg-[#00ff9d]/10 rounded-lg animate-in fade-in zoom-in">
                                    <p className="text-[#00ff9d] font-bold text-xs sm:text-sm">
                                        {session ? "+5 Coins added to your account!" : "Thanks for helping the community!"}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* 3. Favorite Button */}
                        <button
                            onClick={handleFavorite}
                            className={`w-full py-2 sm:py-2.5 font-bold rounded-xl flex items-center justify-center gap-2 border transition-colors text-sm ${isFav
                                ? "bg-red-500/10 border-red-500 text-red-500 hover:bg-red-500/20"
                                : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white"
                                }`}
                        >
                            <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} aria-hidden="true" />
                            {isFav ? "Saved to Favorites" : "Save to Favorites"}
                        </button>

                        {/* Login Alert - Shows under Save to Favorites */}
                        {showLoginAlert && (
                            <div className="p-3 sm:p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-left animate-in fade-in slide-in-from-top-2">
                                <div className="flex items-start gap-2 sm:gap-3">
                                    <div className="p-1.5 sm:p-2 bg-red-500/20 rounded-full">
                                        <Heart className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" aria-hidden="true" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-bold text-white text-xs sm:text-sm">Login Required</p>
                                        <p className="text-xs text-gray-400 mt-0.5 sm:mt-1">
                                            Please sign in to save coupons.
                                        </p>
                                        <a
                                            href="/login"
                                            className="inline-block mt-2 text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-lg transition-colors"
                                        >
                                            Sign In
                                        </a>
                                    </div>
                                    <button type="button" aria-label="Dismiss" onClick={() => setShowLoginAlert(false)} className="text-gray-400 hover:text-white">
                                        <X className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* 4. Newsletter Signup */}
                        <ModalNewsletter storeName={store.name} />

                        {/* 5. Close */}
                        <button
                            type="button"
                            onClick={requestClose}
                            className="text-gray-400 hover:text-white text-sm py-2 min-h-[44px]"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </dialog>
    );
}
